import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { MyProfileCard } from "@/components/Profiles";
import { PushToggle } from "@/components/PushToggle";
import { MessagesButton } from "@/components/RideMessages";
import { NotificationsList } from "@/components/rider/Screens";
import { SectionTitle } from "@/components/rider/Nav";
import { RideRow } from "@/components/driver/DriverHome";
import { GPS_LABEL, useDriverGps, useDriverState } from "@/lib/driverGps";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export const useDriverRides = () => useQuery({ queryKey: ["driverRides"], queryFn: () => api.get("/driver/rides").then((r) => r.data), refetchInterval: 10000 });

export function DriverMessages({ onOpen }) {
  const { data: rides } = useDriverRides();
  return (
    <div className="space-y-5" data-testid="driver-messages">
      <h1 className="font-display text-2xl font-bold tracking-tight">Messages</h1>
      <section className="space-y-2">
        <SectionTitle>Ride conversations</SectionTitle>
        {!rides?.length && <p className="text-sm text-muted-foreground">Rider and Admin chats open for your assigned rides.</p>}
        {rides?.map((r) => (
          <div key={r.id} className="space-y-2" data-testid={`driver-chat-${r.id}`}>
            <RideRow ride={r} onOpen={onOpen} />
            <MessagesButton rideId={r.id} className="h-8 text-xs" />
          </div>
        ))}
      </section>
      <NotificationsList />
    </div>
  );
}

function usePermission() {
  const [perm, setPerm] = useState("unknown");
  useEffect(() => {
    navigator.permissions?.query({ name: "geolocation" }).then((p) => {
      setPerm(p.state);
      p.onchange = () => setPerm(p.state);
    }).catch(() => setPerm("unknown"));
  }, []);
  return perm;
}

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-3 border-b border-white/5 py-2 text-sm last:border-0"><span className="text-muted-foreground">{label}</span><span className="text-right font-semibold" data-testid={testid}>{value}</span></div>
);

export function DriverProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: s } = useDriverState();
  const gps = useDriverGps();
  const perm = usePermission();
  return (
    <div className="space-y-4" data-testid="driver-profile">
      <h1 className="font-display text-2xl font-bold tracking-tight">Profile</h1>
      <MyProfileCard mode="driver" />
      <div className="rounded-2xl border border-white/10 bg-card px-4 py-2">
        <Row label="Name" value={user?.name} testid="driver-profile-name" />
        <Row label="Email" value={user?.email} />
        {user?.phone && <Row label="Phone" value={user.phone} />}
        {user?.vehicle && <Row label="Vehicle" value={user.vehicle} />}
        <Row label="Driver approval" value={user?.driver_status ? user.driver_status[0].toUpperCase() + user.driver_status.slice(1) : "Approved (owner)"} testid="driver-approval-status" />
        <Row label="Status" value={s?.online ? "Online" : "Offline"} testid="driver-profile-online" />
        <Row label="Location permission" value={perm === "granted" ? "Allowed" : perm === "denied" ? "Blocked in browser settings" : perm === "prompt" ? "Will ask when you go Online" : "Unknown"} testid="driver-location-permission" />
        <Row label="Live location" value={GPS_LABEL[gps.status]} />
      </div>
      <div className="rounded-2xl border border-white/10 bg-card p-4"><PushToggle /></div>
      <Button variant="outline" className="h-11 w-full rounded-full" onClick={async () => { await logout(); navigate("/login"); }} data-testid="driver-logout-button">
        <LogOut className="mr-2 h-4 w-4" /> Log out
      </Button>
    </div>
  );
}
